import { z } from 'zod';
import { OBJECT_NAMES, type ObjectDef, type ObjectName } from './objects';
import { filterSchema } from './validation';

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

export const WORKFLOW_TRIGGERS = ['created', 'updated', 'created_or_updated', 'date'] as const;
export type WorkflowTrigger = (typeof WORKFLOW_TRIGGERS)[number];
export const WORKFLOWS_PER_OBJECT = 50;
export const ACTIONS_PER_WORKFLOW = 10;

const uuid = z.string().uuid();
/** "owner" = the record's owner; otherwise a specific user. */
const recipient = z.union([z.literal('owner'), uuid]);

export const workflowActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('update_field'), field: z.string().min(1).max(64), value: z.unknown() }).strict(),
  z.object({
    type: z.literal('create_task'),
    subject: z.string().trim().min(1).max(255),
    dueInDays: z.number().int().min(0).max(365).default(0),
    priority: z.enum(['high', 'normal', 'low']).default('normal'),
    assignTo: recipient.default('owner'),
    description: z.string().max(4000).optional(),
  }).strict(),
  z.object({ type: z.literal('notify'), to: recipient.default('owner'), title: z.string().trim().min(1).max(200), body: z.string().max(1000).optional(), email: z.boolean().default(false) }).strict(),
  z.object({
    type: z.literal('send_email'),
    /** owner, a user, or the record's own Email field (contacts and leads). */
    to: z.union([z.literal('owner'), z.literal('record_email'), uuid]),
    subject: z.string().trim().min(1).max(200),
    body: z.string().min(1).max(10000),
  }).strict(),
  z.object({ type: z.literal('webhook'), endpointId: uuid }).strict(),
]);
export type WorkflowAction = z.infer<typeof workflowActionSchema>;

export const workflowSaveSchema = z
  .object({
    objectName: z.enum(OBJECT_NAMES),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).nullable().optional(),
    trigger: z.enum(WORKFLOW_TRIGGERS),
    /**
     * For updates: 'always' runs on every save that matches the conditions; 'became_true' only when
     * a save makes a record match that didn't before (avoids repeating actions on every edit).
     */
    when: z.enum(['always', 'became_true']).default('became_true'),
    /** Date trigger: run on the day `field` + `offsetDays` (negative = before). Checked once a day. */
    dateField: z.string().max(64).nullable().optional(),
    offsetDays: z.number().int().min(-365).max(365).default(0),
    conditions: z.array(filterSchema).max(20).default([]),
    actions: z.array(workflowActionSchema).min(1).max(ACTIONS_PER_WORKFLOW),
    isActive: z.boolean().default(true),
  })
  .strict()
  .refine((w) => w.trigger !== 'date' || !!w.dateField, { path: ['dateField'], message: 'Choose the date field the schedule is based on' });
export type WorkflowInput = z.infer<typeof workflowSaveSchema>;

export interface Workflow extends WorkflowInput {
  id: string;
  createdByName: string;
  updatedAt: string;
  version: number;
}

export interface WorkflowRun {
  id: string;
  workflowId: string;
  workflowName: string;
  objectName: ObjectName;
  recordId: string;
  status: 'pending' | 'succeeded' | 'failed';
  attempts: number;
  log: { action: string; ok: boolean; message?: string }[];
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/** Structural checks against an object's fields (the server also validates update values by type). */
export function workflowIssues(w: WorkflowInput, obj: ObjectDef): string[] {
  const out: string[] = [];
  const field = (n: string) => obj.fields.find((f) => f.name === n);
  for (const c of w.conditions) if (!field(c.field)) out.push(`Unknown condition field "${c.field}"`);
  if (w.trigger === 'date') {
    const f = w.dateField ? field(w.dateField) : undefined;
    if (!f || (f.type !== 'date' && f.type !== 'datetime')) out.push('The schedule needs a date field');
  }
  for (const a of w.actions) {
    if (a.type === 'update_field') {
      const f = field(a.field);
      if (!f || f.readOnly || f.type === 'stage' || f.lookup === 'pipeline') out.push(`"${a.field}" can't be set by a workflow`);
    }
    if (a.type === 'send_email' && a.to === 'record_email' && !field('email')) out.push(`${obj.pluralLabel} have no Email field to send to`);
  }
  return out;
}

/**
 * Fills {{field}} merge fields from a record ({{name}}, {{amount}}, {{cf_region}}, …).
 * Unknown fields become empty. Values are inserted as plain text.
 */
export function mergeFields(template: string, rec: Record<string, unknown>): string {
  return template.replace(/\{\{\s*([A-Za-z_][A-Za-z0-9_]{0,63})\s*\}\}/g, (_, name: string) => {
    // Own properties only: {{constructor}} must not reach Object.prototype.
    const v = Object.hasOwn(rec, name) ? rec[name] : undefined;
    return v === null || v === undefined ? '' : String(v);
  });
}

// ---------------------------------------------------------------------------
// Outbound webhooks
// ---------------------------------------------------------------------------

/** Event types: `<object>.created|updated|deleted|restored`, plus `workflow.triggered` and `ping`. */
export const RECORD_EVENTS = ['created', 'updated', 'deleted', 'restored'] as const;
export const WEBHOOK_EVENT_CHOICES = [
  ...OBJECT_NAMES.flatMap((o) => RECORD_EVENTS.map((e) => `${o}.${e}`)),
  'workflow.triggered',
] as const;

export const webhookEndpointSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    url: z.string().trim().url().max(2000).refine((u) => /^https?:\/\//i.test(u), 'Use a web address (https://…)'),
    events: z.array(z.string().regex(/^(\*|account|contact|lead|opportunity|workflow)\.(\*|created|updated|deleted|restored|triggered)$/)).min(1).max(50),
    isActive: z.boolean().default(true),
  })
  .strict();

export interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  createdAt: string;
  /** Last 24 hours. */
  recent: { succeeded: number; failed: number; pending: number };
}

export interface WebhookDelivery {
  id: string;
  eventType: string;
  status: 'pending' | 'succeeded' | 'failed';
  attempts: number;
  lastStatusCode: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
}

/** Does an endpoint's subscription list match an event type? Supports `*.created`, `account.*` and `*.*`. */
export function eventMatches(subscriptions: readonly string[], type: string): boolean {
  const [o, e] = type.split('.');
  return subscriptions.some((s) => {
    const [so, se] = s.split('.');
    return (so === '*' || so === o) && (se === '*' || se === e);
  });
}

/** The signed string for `X-CRM-Signature: t=<unix seconds>,v1=<hex HMAC-SHA256>`. */
export const signaturePayload = (timestamp: number, body: string) => `${timestamp}.${body}`;

// ---------------------------------------------------------------------------
// Daily digest
// ---------------------------------------------------------------------------

export const digestSettingsSchema = z.object({ hourUtc: z.number().int().min(0).max(23).nullable() }).strict();
